import type { TableOfContentsItem } from "@generalapplications/ui";

import type { RichContentBlock } from "@/content/contentSchema";

/** A heading's words as an element id: lower case, words joined by hyphens. */
export function headingSlug(text: string): string {
  const slug = text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "section";
}

/**
 * The id of every H2 in an article, by its index among the blocks, each one
 * unique within the article (a second "Summary" becomes `summary-2`). The
 * headings carry these ids, and the table of contents links to them.
 */
export function sectionIds(
  blocks: readonly RichContentBlock[],
): Map<number, string> {
  const ids = new Map<number, string>();
  const seen = new Map<string, number>();
  blocks.forEach((block, index) => {
    if (block.type !== "heading" || block.level !== 2) return;
    const slug = headingSlug(block.text);
    const count = (seen.get(slug) ?? 0) + 1;
    seen.set(slug, count);
    ids.set(index, count === 1 ? slug : `${slug}-${count}`);
  });
  return ids;
}

/** The article's sections, for its table of contents: the H2s in order. */
export function articleSections(
  blocks: readonly RichContentBlock[],
): TableOfContentsItem[] {
  return [...sectionIds(blocks)].map(([index, id]) => {
    const block = blocks[index];
    return { id, label: block?.type === "heading" ? block.text : id };
  });
}
